<template>
	<div>
		<button-loading-component
			label="Автопланирование работ"
			:loading="asyncOperationPending"
			:disabled="!order.jobs?.length || !order.executiveEntityBranch?.id"
			@click="onAutoScheduleJobsClick"
		/>
		<div style="display: block; margin-top: .3rem;">		
			<el-text type="info" size="small">
				Начало планирования: {{dateFormatter.dateTime(order.scheduledStartDate, true, true, true, ', ')}}
			</el-text>
		</div>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import OrderModel from '@/models/OrderModel';
import { Warning } from '@element-plus/icons-vue';
import ButtonLoadingComponent from '@/components/ButtonLoadingComponent.vue';
import axiosHelper from '@/helpers/axiosHelper';
import store from '@/store';
import OrderJobModel from '@/models/OrderJobModel';
import OrderJobAssigneeModel from '@/models/OrderJobAssigneeModel';
import dateOnlyHelper from '@/helpers/dateOnlyHelper';
import { ElMessageBox, ElNotification } from 'element-plus';
import { date } from '@/helpers/formatters';
import ApplicationTab from '@/models/ApplicationTab';
import TabsDictionary from '@/variables/tabs';

@Options({
	props: {
		order: {
			type: OrderModel,
			default: null
		}
	},
	emits: ['scheduled'],
	components: {
		Warning,
		ButtonLoadingComponent
	}
})
export default class OrderAutoScheduleJobsComponent extends Vue {
	/** const */
	dateFormatter = date;

	/** props */
	order!: OrderModel;

	/** view-models */
	asyncOperationPending = false;
	isAutoScheduleButtonDisabled = false;

	onAutoScheduleJobsClick() {
		const branchId = this.order.executiveEntityBranch?.id;
		if (!branchId) {
			return;
		}

		this.asyncOperationPending = true;

		this.isSchedulingAvailAsync(branchId!, this.order.scheduledStartDate)
			.then((isSchedulingAvailable) => {

				// no employees were assigned on date:
				// notify user to assign employees first and quit execution
				if (!isSchedulingAvailable) {
					this.asyncOperationPending = false;
					
					const dateString = date.dateTime(
						this.order.scheduledStartDate,
						false,
						true);

					ElMessageBox.confirm(
						`На ${dateString} не было назначено исполнителей`, //`,
						'Error',
						{
							showClose: true,
							confirmButtonText: 'Закрыть вкладку',
							cancelButtonText: 'Назначить исполнителей',
							type: 'error',
						}
					)
					.catch(() => {
						store.commit('addTab', TabsDictionary.employees);
						const newTab = store.getters.newlyAddedTab as ApplicationTab;		
						store.commit('changeTab', newTab.id);
					});

					return;				
				}

				this.requestAutoScheduleJobsAsync(this.order)
					.then(scheduledJobsAssignees =>
						this.emitScheduleCompleted(scheduledJobsAssignees)
					)
					.catch(e => {
						ElNotification({
							message: 'Ошибка. Не удалось выполнить авто-планирование заказа',
							type: 'warning',
						});
						console.error('Error auto-scheduling order', this.order, e);
					})
					.finally(() => this.asyncOperationPending = false);
			});
	}

	emitScheduleCompleted(scheduledJobsAssignees: Array<OrderJobAssigneeModel>) {		
		this.$emit('scheduled', scheduledJobsAssignees);
	}

	/**
	 * request auto-scheduling jobs for an @param {OrderModel} order
	 */
	async requestAutoScheduleJobsAsync(order: OrderModel) : Promise<Array<OrderJobAssigneeModel>> {		
		const orderJobsDto = order.jobs!.map((oj, ojIndex) => {
			const orderJob = new OrderJobModel(oj);
			if (!oj.id) {
				orderJob.id = ojIndex;
			}

			if (oj.assignees?.length) {
				orderJob.assignees = oj.assignees.map((assignee, assigneeIndex) => {
					let orderJobAssignee = new OrderJobAssigneeModel(assignee);
					
					if (orderJobAssignee.employee?.user?.id) {
						orderJobAssignee.employee.user = null;	
					}

					if (!assignee.id) {				
						orderJobAssignee.id = assigneeIndex;						
					}

					return orderJobAssignee;
				});		
			}

			return orderJob;
		});

		const getManagersHttpClient = axiosHelper.createInstance(store.getters.jwt);
		const response = await getManagersHttpClient.put<Array<OrderJobAssigneeModel>>('/api/employeesJobsSchedule/auto', {
			id: order.id,
			scheduledStartDate: order.scheduledStartDate,
			jobs: orderJobsDto,
			executiveEntityBranchId: order.executiveEntityBranch!.id
		});

		return response.data;
	}

	async isSchedulingAvailAsync(branchId: number, date: Date) {
		try {
			const dateOnly = dateOnlyHelper.date(date);
			const getManagersHttpClient = axiosHelper.createInstance(store.getters.jwt);
			const response = await getManagersHttpClient.get(`/api/employeesJobsSchedule/avail/${branchId}/${dateOnly}`);
			return response.status === 200;			
		} catch (e: any) {
			return false;
		}
	}
}
</script>